import { api } from '../core';
import { BUNDLE_FILTER_OPTIONS_TAG } from '../constants';
import { RootState, setBundleFilterOptions, setEnabledStates, setSelectedValues } from '@/store';
import { PROVIDERS_FILTER_GROUP_NAME } from '@/constants/filters';

interface FilterOptionDate {
  displayType: 'dates';
  endDate: string;
  groupName: 'dates';
  startDate: string;
}

interface FilterOptionDropdownOption {
  id: string;
  value: string;
}

interface FilterOptionDropdown {
  displayType: 'drop_down';
  groupName: string;
  groupType: 'text_label' | 'page_label';
  options: FilterOptionDropdownOption[];
}

interface FilterOptionCheckboxOption {
  id: string;
  value: string;
}

interface FilterOptionCheckbox {
  displayType: 'checkbox';
  groupName: string;
  groupType: 'page_label';
  options: FilterOptionCheckboxOption[];
}

export type BundleFilterOptions = FilterOptionDate | FilterOptionDropdown | FilterOptionCheckbox;

export type BundleFilterOptionsResponse = {
  filterOptions: BundleFilterOptions[];
};

type GetBundleFilterOptionsProps = {
  bundleId: string;
  abortController?: AbortController;
};

export type EnabledStates = { [key: string]: boolean };
export type SelectedValues = { [key: string]: string | string[] | Date[] };

export const getBundleFilterOptionsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getBundleFilterOptions: builder.query<BundleFilterOptionsResponse, GetBundleFilterOptionsProps>({
      query: ({ bundleId, abortController = new AbortController() }: GetBundleFilterOptionsProps) => ({
        url: `bundle/filter-options?bundle_id=${bundleId}`,
        method: 'GET',
        signal: abortController.signal,
      }),
      providesTags: (_result, _err, { bundleId }) => [{ type: BUNDLE_FILTER_OPTIONS_TAG, id: bundleId }],
      async onQueryStarted(_arg, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled;
          const state = getState() as RootState;

          if (data?.filterOptions && state.sharedData.activeBundleType) {
            dispatch(
              setBundleFilterOptions({
                key: state.sharedData.activeBundleType,
                data: data.filterOptions,
              }),
            );

            const initialEnabledStates = data.filterOptions.reduce((acc, option) => {
              if (option.displayType === 'checkbox') {
                return acc;
              }

              acc[option.groupName] = false;
              return acc;
            }, {} as EnabledStates);

            // If the store already has enabledStates for the active bundle type
            // and it's not an empty object, prefer those values instead of
            // overwriting them with the server-provided defaults. Otherwise
            // use the defaults computed from the response.
            const existingBundleState = state.bundleFilterOptions[state.sharedData.activeBundleType];
            const existingEnabledStates = existingBundleState?.enabledStates ?? {};
            const enabledToSet =
              Object.keys(existingEnabledStates).length > 0 ? existingEnabledStates : initialEnabledStates;

            dispatch(
              setEnabledStates({
                key: state.sharedData.activeBundleType,
                data: enabledToSet,
              }),
            );

            const initialSelectedValues = data.filterOptions.reduce((acc, option) => {
              if (option.displayType === 'dates') {
                acc[option.groupName] = [];
              }
              return acc;
            }, {} as SelectedValues);

            const existingSelectedValues = existingBundleState?.selectedValues ?? {};

            // Build a set of valid group names from the response
            const validGroupNames = new Set<string>(data.filterOptions.map((opt) => opt.groupName));

            // If we have existing selected values, remove keys not in validGroupNames
            const prunedExistingSelectedValues = Object.keys(existingSelectedValues).reduce((acc, key) => {
              if (validGroupNames.has(key)) {
                // Find the corresponding filter option from backend
                const backendOption = data.filterOptions.find((opt) => opt.groupName === key);

                if (!backendOption) return acc;

                const existingValue = existingSelectedValues[key];

                // Validate options for drop_down and checkbox
                if (backendOption.displayType === 'drop_down' || backendOption.displayType === 'checkbox') {
                  const isProvidersFilter = backendOption.groupName === PROVIDERS_FILTER_GROUP_NAME;
                  const backendOptionIds = new Set(
                    backendOption.options
                      .map((opt: { id: string; value: string; providerId?: string }) =>
                        isProvidersFilter ? opt.providerId : opt.id,
                      )
                      .filter((id): id is string => id != null),
                  );

                  if (Array.isArray(existingValue)) {
                    // Filter out values not present in backend options, only for string values
                    const filtered = existingValue.filter(
                      (v): v is string => typeof v === 'string' && backendOptionIds.has(v),
                    );

                    if (filtered.length > 0) {
                      acc[key] = filtered;
                    }
                  } else if (typeof existingValue === 'string') {
                    if (backendOptionIds.has(existingValue)) {
                      acc[key] = existingValue;
                    }
                  }
                } else if (backendOption.displayType === 'dates') {
                  // For dates, keep as is (or add further validation if needed)
                  acc[key] = existingValue;
                }
              }
              return acc;
            }, {} as SelectedValues);

            // If prunedExistingSelectedValues is non-empty, use it; otherwise fall back to initialSelectedValues
            // For each group, if pruned value is missing or empty, fall back to initialSelectedValues
            const selectedValuesToSet = { ...initialSelectedValues };
            Object.keys(prunedExistingSelectedValues).forEach((key) => {
              const value = prunedExistingSelectedValues[key];
              if (
                (Array.isArray(value) && value.length > 0) ||
                (typeof value === 'string' && value) ||
                value instanceof Date
              ) {
                selectedValuesToSet[key] = value;
              }
            });

            dispatch(
              setSelectedValues({
                key: state.sharedData.activeBundleType,
                data: selectedValuesToSet,
              }),
            );
          }
        } catch (error) {
          if (error instanceof Error && error.name === 'AbortError') {
            console.log('Request was aborted');
          } else {
            console.error('Error in getBundleFilterOptions in onQueryStarted:', error);
          }
        }
      },
    }),
  }),
});

/**
 * Export hooks for usage in functional components, which are
 * auto-generated based on the defined endpoints
 */
export const { useLazyGetBundleFilterOptionsQuery } = getBundleFilterOptionsApi;
